import React, { FC, lazy, Suspense } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as CopyIcon } from '@blink/assets/src/icons/copy.svg';
import { WalletHelper } from '@blink/components/src/utils';

import { WalletIcon } from '@/shared/ui/WalletIcon';
import { Store } from '@/shared/store';
import { disconnectWalletSaga } from '@/shared/store/wallets/actions';
import { hideModalAction, showModalAction } from '@/shared/store/modals/actions';
import { ModalName } from '@/shared/store/modals/constants';
import { Button, ButtonVariant, ViewOnEtherscanButton } from '@/shared/ui';
import { useCopyToClipboard } from '@/shared/hooks/useCopyToClipboard';
import useLocalFeatureFlag from '@/shared/hooks/useLocalFeatureFlag';
import { Features } from '@/shared/configs/features';

export const BaseWalletInfoModal: FC = () => {
    const { address, type } = useSelector((state: Store) => ({
        address: state.wallets.active?.address,
        type: state.wallets.active?.type,
    }));
    const dispatch = useDispatch();

    const handleCopyAddress = useCopyToClipboard(address);

    const handleDisconnectWallet = () => {
        dispatch(disconnectWalletSaga());
        dispatch(hideModalAction());
    };

    const handleChangeWallet = () =>
        dispatch(showModalAction({ modalName: ModalName.CONNECT_WALLET_MODAL }));

    return (
        <div className='mt-7'>
            <div className='flex items-center min-w-[464px]'>
                <div className='flex justify-center items-center gap-3 self-stretch'>
                    <WalletIcon walletType={type} />
                </div>
                <div className='flex flex-col justify-center items-start min-h-[48px] px-4 py-[10px] flex-[1_0_0] text-[16px] font-semibold font-[Inter]'>
                    {WalletHelper.addressShortName(address)}
                </div>
                <div className='flex justify-center items-center gap-3 self-stretch'>
                    <Button variant={ButtonVariant.Primary} onClick={handleChangeWallet}>
                        Change
                    </Button>
                    <Button variant={ButtonVariant.Secondary} onClick={handleDisconnectWallet}>
                        Disconnect
                    </Button>
                </div>
            </div>
            <div className='h-px self-stretch flex flex-col items-start bg-border-opaque my-5' />
            <div className='flex pt-[4px] self-stretch'>
                <Button variant={ButtonVariant.Link} onClick={handleCopyAddress}>
                    <CopyIcon />
                    <div>Copy Address</div>
                </Button>
                <ViewOnEtherscanButton resource={{ address: address }} />
            </div>
        </div>
    );
};

const LazyWalletInfoModal = lazy(() =>
    import('./v2/WalletInfoModal').then((module) => ({
        default: module.WalletInfoModal,
    })),
);

export const WalletInfoModal: FC = () => {
    const isNewWalletConnect = useLocalFeatureFlag(Features.NewWalletConnect, true);

    if (!isNewWalletConnect) {
        return <BaseWalletInfoModal />;
    }

    return (
        <Suspense fallback={null}>
            <LazyWalletInfoModal />
        </Suspense>
    );
};
